import Icons from './Icons.tsx';
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Accordion, AccordionSummary, AccordionDetails,
  Stack, Button, ButtonGroup, MenuItem, Slider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { usePositionEditing } from './PositionEditingProvider.tsx';
import type { Position } from '../types/position.ts';
import { apiFetch } from "../utils/api.ts";
import { useTagsRegistry } from './TagsProvider.tsx';
import { buildTagTree } from '../utils/tags.ts';
import { bulkAssignTag, bulkClearCategory } from '../utils/tagsApi.ts';

function toISOStringWithTimezone(date) {
  const timezoneOffset = date.getTimezoneOffset();

  const timezoneOffsetSign = timezoneOffset >= 0 ? "-" : "+";
  const timezoneOffsetHours = String(Math.floor(Math.abs(timezoneOffset) / 60)).padStart(2, "0");
  const timezoneOffsetMinutes = String(Math.abs(timezoneOffset) % 60).padStart(2, "0");

  const dateAdjustedToTimezoneOffset = new Date(date.getTime() - timezoneOffset * 60 * 1000);

  const isoStringWithTimezone = dateAdjustedToTimezoneOffset.toISOString().replace("Z", "") + timezoneOffsetSign + timezoneOffsetHours + ":" + timezoneOffsetMinutes;
  return isoStringWithTimezone;
}

function isoToInput(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  // keep local time in input
  return toISOStringWithTimezone(d).slice(0, 16);
}
function inputToIso(localValue: string) {
  if (!localValue) return '';
  return new Date(localValue).toISOString();
}

const columnNames = ['travel_mode'] as const;
const travelModeOptions = ['Stationary', 'Foot', 'Bike', 'Car', 'Bus', 'Tram', 'Metro', 'Train', 'Boat', 'Plane'] as const;

const TagValuePicker: React.FC<{
  categoryId: number;
  value: string;
  onChange: (v: string) => void;
  tags: import('../types/position.ts').Tag[];
}> = ({ value, onChange, tags }) => {
  const tree = React.useMemo(() => buildTagTree(tags), [tags]);
  const renderOptions = (nodes: ReturnType<typeof buildTagTree>, depth: number): React.ReactNode[] =>
    nodes.flatMap(node => [
      <MenuItem key={node.tag.id} value={String(node.tag.id)} sx={{ pl: 2 + depth * 2 }}>
        {node.tag.tag}
      </MenuItem>,
      ...(node.children.length > 0 ? renderOptions(node.children, depth + 1) : []),
    ]);
  return (
    <TextField
      label="Tag"
      select
      fullWidth
      size="small"
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      <MenuItem value="__clear__"><em>(clear)</em></MenuItem>
      {renderOptions(tree, 0)}
    </TextField>
  );
};

type Props = {
  positions: Position[];
  filterStartTime: string;
  filterEndTime: string;
  closeDrawerEditing: () => void;
  unpinDrawerEditing: () => void;
  drawerOpen: boolean;
  onPositionsUpdated: (positionIds: number[], targetColumn: string, targetValue: string) => void;
};

const PositionEditing: React.FC<Props> = ({
  positions,
  filterStartTime,
  filterEndTime,
  closeDrawerEditing,
  unpinDrawerEditing,
  drawerOpen,
  onPositionsUpdated,
}) => {
  const editing = usePositionEditing();
  const { registry, refresh: refreshTags } = useTagsRegistry();
  const [targetValue, setTargetValue] = useState('');
  const [targetColumn, setTargetColumn] = useState('');
  const [sliderStartTimeMs, setSliderStartTimeMs] = useState<number | null>(null);
  const [sliderEndTimeMs, setSliderEndTimeMs] = useState<number | null>(null);
  const sliderUpdatingRef = useRef({ start: false, end: false });
  const holdTimerRef = useRef<number | null>(null);
  const prevDrawerOpen = useRef<boolean>(false);

  // Normalize for display/compute (always s <= e)
  const [s, e] = useMemo(() => {
    const a = editing.startIndex, b = editing.endIndex;
    if (a == null || b == null) return [a, b];
    return a <= b ? [a, b] : [b, a];
  }, [editing.startIndex, editing.endIndex]);

  const start: Position | null = s != null ? positions[s] : null;
  const end:   Position | null = e != null ? positions[e] : null;
  const count = (s != null && e != null) ? (e - s + 1) : '';

  const START_SCALE = 3;
  const END_SCALE = 3;

  const startDate = useMemo(() => {
    return positions.length > 0 ? new Date(positions[0].time).getTime() : 0;
  }, [positions]);

  const endDate = useMemo(() => {
    return positions.length > 0 ? new Date(positions[positions.length - 1].time).getTime() : 0;
  }, [positions]);

  const filterStartMs = useMemo(() => {
    const parsed = new Date(filterStartTime).getTime();
    return Number.isFinite(parsed) ? parsed : startDate;
  }, [filterStartTime, startDate]);

  const filterEndMs = useMemo(() => {
    const parsed = new Date(filterEndTime).getTime();
    return Number.isFinite(parsed) ? parsed : endDate;
  }, [filterEndTime, endDate]);

  const clampedSliderStartTimeMs = useMemo(() => {
    if (sliderStartTimeMs == null) return filterStartMs;
    return Math.min(Math.max(sliderStartTimeMs, filterStartMs), filterEndMs);
  }, [sliderStartTimeMs, filterStartMs, filterEndMs]);

  const clampedSliderEndTimeMs = useMemo(() => {
    if (sliderEndTimeMs == null) return filterEndMs;
    return Math.min(Math.max(sliderEndTimeMs, filterStartMs), filterEndMs);
  }, [sliderEndTimeMs, filterStartMs, filterEndMs]);

  // Clamp indices if data shrinks
  useEffect(() => {
    const maxIndex = positions.length - 1;
    if ((editing.startIndex != null && editing.startIndex > maxIndex) ||
        (editing.endIndex != null && editing.endIndex > maxIndex)) {
      editing.clear();
    }
  }, [positions.length, editing]);

  useEffect(() => {
    if (drawerOpen && !prevDrawerOpen.current && positions.length > 0 && (editing.startIndex == null || editing.endIndex == null)) {
      editing.setSelectionRange(0, positions.length - 1);
      setSliderStartTimeMs(filterStartMs);
      setSliderEndTimeMs(filterEndMs);
      editing.resetPopupSelectionTarget();
    }
    prevDrawerOpen.current = drawerOpen;
  }, [drawerOpen, positions.length, editing, editing.startIndex, editing.endIndex, filterStartMs, filterEndMs]);

  useEffect(() => {
    if (s != null && positions[s]) {
      const selectedStartMs = new Date(positions[s].time).getTime();
      setSliderStartTimeMs(prev => prev === selectedStartMs ? prev : selectedStartMs);
    }
    if (e != null && positions[e]) {
      const selectedEndMs = new Date(positions[e].time).getTime();
      setSliderEndTimeMs(prev => prev === selectedEndMs ? prev : selectedEndMs);
    }
  }, [s, e, positions]);

  useEffect(() => {
    setSliderStartTimeMs(prev => {
      if (prev == null) return filterStartMs;
      return Math.min(Math.max(prev, filterStartMs), filterEndMs);
    });
    setSliderEndTimeMs(prev => {
      if (prev == null) return filterEndMs;
      return Math.min(Math.max(prev, filterStartMs), filterEndMs);
    });
  }, [filterStartMs, filterEndMs]);

  const setStartId = useCallback((v: string) => {
    const id = v === '' ? NaN : Number(v);
    if (!Number.isFinite(id)) return;
    const index = positions.findIndex(p => p.id === id);
    if (index >= 0) {
      const selectedTime = new Date(positions[index].time).getTime();
      setSliderStartTimeMs(selectedTime);
      editing.setStartById(id);
    }
  }, [positions, editing]);

  const setEndId = useCallback((v: string) => {
    const id = v === '' ? NaN : Number(v);
    if (!Number.isFinite(id)) return;
    const index = positions.findIndex(p => p.id === id);
    if (index >= 0) {
      const selectedTime = new Date(positions[index].time).getTime();
      setSliderEndTimeMs(selectedTime);
      editing.setEndById(id);
    }
  }, [positions, editing]);

  const setStartTime = useCallback((v: string) => {
    const iso = inputToIso(v);
    if (!iso) return;
    const selectedTime = new Date(iso).getTime();
    setSliderStartTimeMs(selectedTime);
    editing.setStartByTime(iso);
  }, [editing]);

  const setEndTime = useCallback((v: string) => {
    const iso = inputToIso(v);
    if (!iso) return;
    const selectedTime = new Date(iso).getTime();
    setSliderEndTimeMs(selectedTime);
    editing.setEndByTime(iso);
  }, [editing]);

  const setCount = useCallback((v: string) => {
    const c = v === '' ? NaN : Number(v);
    if (Number.isFinite(c)) editing.setCountFromStart(c);
  }, [editing]);

  const toStartSlider = useMemo(() => {
    if (filterEndMs <= filterStartMs) return 0;
    const maxStartTime = Math.min(clampedSliderEndTimeMs, filterEndMs);
    const totalSpan = Math.max(1, maxStartTime - filterStartMs);
    const normalized = (clampedSliderStartTimeMs - filterStartMs) / totalSpan;
    return 1 - Math.pow(1 - Math.max(0, Math.min(1, normalized)), 1 / START_SCALE);
  }, [clampedSliderStartTimeMs, clampedSliderEndTimeMs, filterStartMs, filterEndMs]);

  const toEndSlider = useMemo(() => {
    const minEndTime = Math.max(clampedSliderStartTimeMs, filterStartMs);
    const totalSpan = Math.max(1, filterEndMs - minEndTime);
    const normalized = (clampedSliderEndTimeMs - minEndTime) / totalSpan;
    return Math.pow(Math.max(0, Math.min(1, normalized)), 1 / END_SCALE);
  }, [clampedSliderEndTimeMs, clampedSliderStartTimeMs, filterStartMs, filterEndMs]);

  const getNearestIndexByTime = useCallback((timeMs: number, minIndex: number, maxIndex: number) => {
    let nearest = minIndex;
    let nearestDiff = Math.abs(new Date(positions[minIndex].time).getTime() - timeMs);
    for (let i = minIndex + 1; i <= maxIndex; i += 1) {
      const diff = Math.abs(new Date(positions[i].time).getTime() - timeMs);
      if (diff < nearestDiff) {
        nearestDiff = diff;
        nearest = i;
      }
    }
    return nearest;
  }, [positions]);

  const formatTimeLabel = useCallback((timeMs: number) => {
    const date = new Date(timeMs);
    return date.toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  }, []);

  const handleStartSliderChange = useCallback((_: Event, value: number | number[]) => {
    const raw = Array.isArray(value) ? value[0] : value;
    const sliderValue = Math.max(0, Math.min(1, raw));
    const maxStartTime = Math.min(clampedSliderEndTimeMs, filterEndMs);
    const totalSpan = Math.max(1, maxStartTime - filterStartMs);
    const desiredTimeMs = filterStartMs + (1 - Math.pow(1 - sliderValue, START_SCALE)) * totalSpan;
    sliderUpdatingRef.current.start = true;
    setSliderStartTimeMs(desiredTimeMs);
    editing.setStartByTime(new Date(desiredTimeMs).toISOString());
  }, [clampedSliderEndTimeMs, filterStartMs, filterEndMs, editing]);

  const handleEndSliderChange = useCallback((_: Event, value: number | number[]) => {
    const raw = Array.isArray(value) ? value[0] : value;
    const sliderValue = Math.max(0, Math.min(1, raw));
    const minEndTime = Math.max(clampedSliderStartTimeMs, filterStartMs);
    const totalSpan = Math.max(1, filterEndMs - minEndTime);
    const desiredTimeMs = minEndTime + Math.pow(sliderValue, END_SCALE) * totalSpan;
    sliderUpdatingRef.current.end = true;
    setSliderEndTimeMs(desiredTimeMs);
    editing.setEndByTime(new Date(desiredTimeMs).toISOString());
  }, [clampedSliderStartTimeMs, filterStartMs, filterEndMs, editing]);

  const adjustStartIndex = useCallback((delta: number) => {
    if (s == null) return;
    const nextIndex = Math.min(Math.max(0, s + delta), positions.length - 1);
    editing.setStartByIndex(nextIndex);
  }, [s, positions.length, editing]);

  const adjustEndIndex = useCallback((delta: number) => {
    if (e == null) return;
    const nextIndex = Math.min(Math.max(0, e + delta), positions.length - 1);
    editing.setEndByIndex(nextIndex);
  }, [e, positions.length, editing]);

  const stopHold = useCallback(() => {
    if (holdTimerRef.current != null) {
      window.clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const clickSuppressionRef = useRef(false);

  const createHoldHandlers = useCallback((action: () => void) => ({
    onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      action();
      clickSuppressionRef.current = true;
      if (holdTimerRef.current != null) {
        window.clearInterval(holdTimerRef.current);
      }
      holdTimerRef.current = window.setTimeout(() => {
        holdTimerRef.current = window.setInterval(action, 500);
      }, 500);
    },
    onPointerUp: stopHold,
    onPointerLeave: stopHold,
    onPointerCancel: stopHold,
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      if (clickSuppressionRef.current) {
        event.preventDefault();
        event.stopPropagation();
        clickSuppressionRef.current = false;
      }
    },
  }), [stopHold]);

  const clear = useCallback(() => {
    editing.clear();
    setTargetValue('');
  }, [editing]);

  // Build the list of position IDs
  const positionIds = React.useMemo<number[] | null>(() => {
    if (s == null || e == null) return null;
    const start = Math.max(0, s);
    const end = Math.min(positions.length - 1, e);
    // assumes positions[i].id exists and is a number
    const ids = positions.slice(start, end + 1).map((p: any) => p.id).filter((id: any) => id != null);
    return ids.length ? Array.from(new Set(ids)) : null;
  }, [positions, s, e]);

  const save = useCallback(async () => {
    if (!positionIds || !targetColumn || !targetValue) return;
    const previousEndId = end?.id;

    if (targetColumn.startsWith('tag:')) {
      const categoryId = Number(targetColumn.slice(4));
      if (targetValue === '__clear__') {
        await bulkClearCategory(positionIds, categoryId);
      } else {
        await bulkAssignTag(positionIds, Number(targetValue));
      }
      await refreshTags();
      onPositionsUpdated(positionIds, targetColumn, targetValue);
    } else {
      const res = await apiFetch("/api/positions/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ positionIds, targetColumn, targetValue }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onPositionsUpdated(positionIds, targetColumn, targetValue);
    }

    if (previousEndId != null) {
      editing.setSelectionRange(
        positions.findIndex(p => p.id === previousEndId),
        positions.findIndex(p => p.id === previousEndId)
      );
    }
  }, [positionIds, targetColumn, targetValue, end, editing, onPositionsUpdated, positions, refreshTags]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Start Point */}
      <Accordion key="start" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content['start'] ?? Icons.drawer.content['datetime']}
            <Typography sx={{ ml: 1 }}>Start Point</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Id"
              type="number"
              InputLabelProps={{ shrink: true }}
              value={start?.id ?? ''}
              onChange={(e) => setStartId(e.target.value)}
            />
            <TextField
              label="Time"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={isoToInput(start?.time)}
              onChange={(e) => setStartTime(e.target.value)}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                size="small"
                sx={{ minWidth: 34, width: 34, px: 0, fontWeight: 'bold' }}
                disabled={s == null || s <= 0}
                {...createHoldHandlers(() => adjustStartIndex(-1))}
              >
                <RemoveIcon fontSize="small" />
              </Button>
              <Slider
                sx={{ flex: 1 }}
                value={toStartSlider}
                min={0}
                max={1}
                step={0.005}
                track="inverted"
                onChange={handleStartSliderChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => {
                  const sliderValue = Math.max(0, Math.min(1, value as number));
                  const maxStartTime = Math.min(clampedSliderEndTimeMs, filterEndMs);
                  const totalSpan = Math.max(1, maxStartTime - filterStartMs);
                  const selectedMs = filterStartMs + (1 - Math.pow(1 - sliderValue, START_SCALE)) * totalSpan;
                  return formatTimeLabel(selectedMs);
                }}
                disabled={positions.length === 0 || e == null}
              />
              <Button
                size="small"
                sx={{ minWidth: 34, width: 34, px: 0, fontWeight: 'bold' }}
                disabled={s == null || s >= positions.length - 1}
                {...createHoldHandlers(() => adjustStartIndex(1))}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* End Point */}
      <Accordion key="end" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content['end'] ?? Icons.drawer.content['datetime']}
            <Typography sx={{ ml: 1 }}>End Point</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Id"
              type="number"
              InputLabelProps={{ shrink: true }}
              value={end?.id ?? ''}
              onChange={(e) => setEndId(e.target.value)}
            />
            <TextField
              label="Time"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={isoToInput(end?.time)}
              onChange={(e) => setEndTime(e.target.value)}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                size="small"
                sx={{ minWidth: 34, width: 34, px: 0, fontWeight: 'bold' }}
                disabled={e == null || e <= 0}
                {...createHoldHandlers(() => adjustEndIndex(-1))}
              >
                <RemoveIcon fontSize="small" />
              </Button>
              <Slider
                sx={{ flex: 1 }}
                value={toEndSlider}
                min={0}
                max={1}
                step={0.005}
                onChange={handleEndSliderChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => {
                  const sliderValue = Math.max(0, Math.min(1, value as number));
                  const minEndTime = Math.max(clampedSliderStartTimeMs, filterStartMs);
                  const totalSpan = Math.max(1, filterEndMs - minEndTime);
                  const selectedMs = minEndTime + Math.pow(sliderValue, END_SCALE) * totalSpan;
                  return formatTimeLabel(selectedMs);
                }}
                disabled={positions.length === 0 || s == null}
              />
              <Button
                size="small"
                sx={{ minWidth: 34, width: 34, px: 0, fontWeight: 'bold' }}
                disabled={e == null || e >= positions.length - 1}
                {...createHoldHandlers(() => adjustEndIndex(1))}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Target Column */}
      <Accordion key="targetColumn" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content['column']}
            <Typography sx={{ ml: 1 }}>Column</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            label="Column"
            select
            fullWidth
            size="small"
            value={targetColumn}
            onChange={e => { setTargetColumn(e.target.value); setTargetValue(''); }}
          >
            {columnNames.map(name => (
              <MenuItem key={name} value={name.toLowerCase()}>
                {name}
              </MenuItem>
            ))}
            {registry.effectiveCategories.map(cat => (
              <MenuItem key={`tag:${cat.id}`} value={`tag:${cat.id}`}>
                Tag: {cat.tag_category}
              </MenuItem>
            ))}
          </TextField>
        </AccordionDetails>
      </Accordion>

      {/* Target Value */}
      <Accordion key="targetValue" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content['value']}
            <Typography sx={{ ml: 1 }}>Value</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {targetColumn.startsWith('tag:') ? (
            <TagValuePicker
              categoryId={Number(targetColumn.slice(4))}
              value={targetValue}
              onChange={setTargetValue}
              tags={Array.from(registry.tags.values()).filter(t => t.category_id === Number(targetColumn.slice(4)))}
            />
          ) : (
            <TextField
              label="Value"
              select
              fullWidth
              size="small"
              value={targetValue}
              onChange={e => setTargetValue(e.target.value)}
            >
              {travelModeOptions.map(option => (
                <MenuItem key={option} value={option.toLowerCase()}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
        </AccordionDetails>
      </Accordion>
      <Button variant="contained" disabled={!start || !end || !targetValue} onClick={save}>
        Update Positions
      </Button>
    </Box>
  );
};

export default PositionEditing;
